import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { cn } from "@/cn";
import { createSession, getHandoffData, prefillAnswers } from "@/api/proposals";
import { useOpportunities } from "@/hooks/useOpportunities";
import type { StreamEvent } from "@sales-ops-amplify/shared";

interface FormField {
  id: string;
  label: string;
  type: "text" | "textarea" | "select" | "file" | "checkbox";
  required: boolean;
  options?: string[];
  value: string;
}

export function ProposalsPage() {
  const [searchParams] = useSearchParams();
  const queryOpportunityId = searchParams.get("opportunityId") ?? "";

  const { data: oppsData } = useOpportunities({ limit: 100, offset: 0 });
  const opportunities = oppsData?.opportunities ?? [];

  const [url, setUrl] = useState("");
  const [opportunityId, setOpportunityId] = useState(queryOpportunityId);
  const [loading, setLoading] = useState(false);
  const [extracted, setExtracted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<FormField[]>([]);

  useEffect(() => {
    if (queryOpportunityId) setOpportunityId(queryOpportunityId);
  }, [queryOpportunityId]);

  useEffect(() => {
    if (!opportunityId || url.trim()) return;
    const opp = opportunities.find((o) => o.id === opportunityId);
    if (opp?.url) setUrl(opp.url);
  }, [opportunityId, opportunities, url]);

  async function handleExtract() {
    if (!url.trim()) return;
    setError(null);
    setLoading(true);

    try {
      const selected = opportunities.find((o) => o.id === opportunityId);
      const created = (await createSession(url.trim(), {
        opportunityId: opportunityId || undefined,
        company: selected?.company,
        dealName: selected?.dealName,
      })) as { sessionId?: string; id?: string; status?: string };
      const id = created.sessionId ?? created.id;
      if (!id) throw new Error("No session id returned");

      await prefillAnswers(id, (event) => {
        const e = event as StreamEvent;
        if (e.type === "error" && "msg" in e) {
          setError(String(e.msg));
        }
      });

      const handoff = await getHandoffData(id);
      if (handoff.fields?.length) {
        setFields(
          handoff.fields.map((f) => ({
            id: f.id,
            label: f.label,
            type: (f.type === "textarea"
              ? "textarea"
              : f.type === "select"
                ? "select"
                : f.type === "checkbox"
                  ? "checkbox"
                  : "text") as FormField["type"],
            required: f.required,
            options: f.options,
            value: handoff.answers[f.id] ?? f.value ?? "",
          })),
        );
      }
      setExtracted(true);
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to start proposal session.",
      );
    } finally {
      setLoading(false);
    }
  }

  function updateField(id: string, value: string) {
    setFields((prev) =>
      prev.map((f) => (f.id === id ? { ...f, value } : f)),
    );
  }

  function handleOpenForm() {
    if (url) {
      window.open(url, "_blank", "noopener noreferrer");
    }
  }

  return (
    <>
      <header className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">Proposals</h1>
        <p className="text-muted-foreground">
          Pre-fill and review proposal forms before submitting on the
          buyer&apos;s site. Bind a session to a deal for context.
        </p>
      </header>

      <div className="mb-6 rounded-lg border border-border bg-card p-5">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Step 1: Deal &amp; URL
        </h2>
        <div className="mb-3">
          <label className="mb-1 block text-xs text-muted-foreground">
            Deal (optional)
          </label>
          <select
            value={opportunityId}
            onChange={(e) => setOpportunityId(e.target.value)}
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand"
          >
            <option value="">No deal linked</option>
            {opportunities.map((o) => (
              <option key={o.id} value={o.id}>
                {o.company} — {o.dealName}
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <input
            type="url"
            placeholder="https://company.com/rfp/proposal-form"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleExtract();
            }}
            className="flex-1 rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
          />
          <button
            onClick={handleExtract}
            disabled={loading || !url.trim()}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-5 py-2 text-sm font-medium text-brand-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                Extracting…
              </>
            ) : (
              "Extract fields"
            )}
          </button>
        </div>
        {error && (
          <p className="mt-3 text-sm text-destructive">{error}</p>
        )}
      </div>

      {extracted && (
        <div className="mb-6 rounded-lg border border-border bg-card p-5">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Step 2: Review answers
            </h2>
            <button
              type="button"
              onClick={handleOpenForm}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-brand hover:bg-muted/20"
            >
              Open form
            </button>
          </div>
          {fields.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No form fields extracted. Open the form and fill manually.
            </p>
          ) : (
            <div className="space-y-4">
              {fields.map((field) => (
                <div key={field.id}>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    {field.label}
                    {field.required && (
                      <span className="ml-1 text-destructive">*</span>
                    )}
                  </label>
                  {field.type === "textarea" ? (
                    <textarea
                      value={field.value}
                      onChange={(e) => updateField(field.id, e.target.value)}
                      rows={3}
                      className={cn(
                        "w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground",
                        "focus:outline-none focus:ring-2 focus:ring-brand",
                      )}
                    />
                  ) : field.type === "select" && field.options ? (
                    <select
                      value={field.value}
                      onChange={(e) => updateField(field.id, e.target.value)}
                      className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand"
                    >
                      <option value="">Select…</option>
                      {field.options.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field.type === "checkbox" ? "text" : "text"}
                      value={field.value}
                      onChange={(e) => updateField(field.id, e.target.value)}
                      className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand"
                    />
                  )}
                </div>
              ))}
            </div>
          )}
          {opportunityId && (
            <p className="mt-4 text-xs text-muted-foreground">
              Linked to{" "}
              <Link
                to={`/deals/${opportunityId}`}
                className="text-brand hover:underline"
              >
                opportunity
              </Link>
              .
            </p>
          )}
        </div>
      )}
    </>
  );
}
